import React from 'react';
import { Icon } from '../icon/icon.tsx';
import { classList } from '../../../utils/string.ts';

type ButtonBaseProps = {
  onClick?: () => void;
  href?: string;
  target?: string;
  className?: string;
  disabled?: boolean;
  type?: 'button' | 'submit' | 'reset';
};

type ButtonProps = ButtonBaseProps & {
  children: React.ReactNode;
  variant?: 'dark' | 'light' | 'primary-contrast';
};

type ButtonBubbleProps = ButtonBaseProps & React.PropsWithChildren;

type ButtonBubbleTextIconProps = ButtonBaseProps &
  React.PropsWithChildren & {
    iconName?: string;
    iconClassName?: string;
    align?: 'left' | 'right';
  };

type ButtonBubbleIconProps = ButtonBaseProps & {
  iconName?: string;
};

export const Button = React.forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps>(
  (
    {
      children,
      onClick,
      href,
      target,
      className = '',
      disabled = false,
      variant = 'dark',
      type = 'button'
    },
    ref
  ) => {
    const Tag = href ? 'a' : 'button';
    return (
      <Tag
        ref={ref as any}
        onClick={onClick}
        href={href}
        target={target}
        className={classList(
          `inline-block cursor-pointer rounded-full px-4 py-1.5 text-sm`,
          disabled && 'pointer-events-none cursor-default opacity-50',
          variant === 'dark' &&
            'text-sk-grey-0 bg-sk-blue-500 focus-visible:bg-sk-grey-900 hover:bg-sk-grey-900',
          variant === 'light' &&
            'text-sk-dark-blue focus-visible:bg-sk-grey-200 hover:bg-sk-grey-200 border-sk-dark-blue border-2 bg-white',
          variant === 'primary-contrast' &&
            'bg-sk-grey-0 focus-visible:bg-sk-grey-200 hover:bg-sk-grey-200 text-sk-blue-500',
          className
        )}
        type={Tag === 'button' ? type : undefined}
      >
        {children}
      </Tag>
    );
  }
);
Button.displayName = 'Button';

export const ButtonBubble = React.forwardRef<
  HTMLButtonElement | HTMLAnchorElement,
  ButtonBubbleProps
>(({ onClick, children, href, className = '', disabled = false }, ref) => {
  const Tag = href ? 'a' : 'button';
  return (
    <Tag
      ref={ref as any}
      onClick={onClick}
      href={href}
      className={`bg-sk-grey-0 text-sk-blue-500 focus-visible:bg-sk-grey-200 hover:bg-sk-grey-200 inline-block cursor-pointer rounded-full px-2 py-1.5 text-sm ${disabled ? 'pointer-events-none cursor-default opacity-50' : ''} ${className}`}
    >
      {children}
    </Tag>
  );
});
ButtonBubble.displayName = 'ButtonBubble';

export const ButtonBubbleTextIcon = React.forwardRef<
  HTMLButtonElement | HTMLAnchorElement,
  ButtonBubbleTextIconProps
>(
  (
    {
      onClick,
      href,
      children,
      className = '',
      disabled = false,
      iconName = '',
      iconClassName = '',
      align = 'right'
    },
    ref
  ) => {
    const Tag = href ? 'a' : 'button';
    const alignLeft = align === 'left';
    const alignRight = align === 'right';

    return (
      <Tag
        ref={ref as any}
        onClick={onClick}
        href={href}
        className={`bg-sk-grey-0 text-sk-blue-500 focus-visible:bg-sk-grey-200 hover:bg-sk-grey-200 inline-flex cursor-pointer items-center gap-1 rounded-full px-2 py-1 text-sm ${disabled ? 'pointer-events-none cursor-default opacity-50' : ''} ${className}`}
      >
        {alignLeft && iconName && (
          <Icon name={iconName} className={`align-bottom text-2xl leading-none ${iconClassName}`} />
        )}
        {children}
        {alignRight && iconName && (
          <Icon name={iconName} className={`align-bottom text-2xl leading-none ${iconClassName}`} />
        )}
      </Tag>
    );
  }
);
ButtonBubbleTextIcon.displayName = 'ButtonBubbleTextIcon';

export const ButtonBubbleIcon = React.forwardRef<HTMLAnchorElement, ButtonBubbleIconProps>(
  ({ onClick, href, className = '', disabled = false, iconName = '' }, ref) => {
    return (
      <a
        ref={ref}
        onClick={onClick}
        href={href}
        className={classList(
          'bg-sk-grey-0 text-sk-red-500 focus-visible:bg-sk-grey-200 hover:bg-sk-grey-200 inline-block h-8 w-8 cursor-pointer content-center justify-center rounded-full text-center',
          disabled && 'pointer-events-none cursor-default opacity-50',
          className
        )}
      >
        <Icon name={iconName} className="align-bottom text-2xl leading-none" />
      </a>
    );
  }
);
ButtonBubbleIcon.displayName = 'ButtonBubbleIcon';

export const TextButton: React.FC<ButtonBubbleProps> = ({
  onClick,
  href,
  children,
  className = '',
  disabled = false
}) => {
  return (
    <a
      onClick={onClick}
      href={href}
      className={`text-sk-red-500 focus-visible:text-sk-red-900 hover:text-sk-red-900 inline-block cursor-pointer ${disabled ? 'pointer-events-none cursor-default opacity-50' : ''} ${className}`}
    >
      {children}
    </a>
  );
};

export const TextIconButton = React.forwardRef<HTMLAnchorElement, ButtonBubbleTextIconProps>(
  (
    {
      onClick,
      href,
      children,
      className = '',
      iconClassName = '',
      disabled = false,
      iconName = '',
      align = 'right'
    },
    ref
  ) => {
    const alignLeft = align === 'left';
    const alignRight = align === 'right';

    return (
      <a
        ref={ref}
        onClick={onClick}
        href={href}
        className={classList(
          'text-sk-red-500 focus-visible:text-sk-red-900 hover:text-sk-red-900 inline-flex cursor-pointer items-center text-sm',
          disabled && 'pointer-events-none cursor-default opacity-50',
          className
        )}
      >
        {alignLeft && children}
        <Icon
          name={iconName}
          className={classList('align-bottom text-2xl leading-none', iconClassName)}
          aria-hidden={true}
        />
        {alignRight && children}
      </a>
    );
  }
);
TextIconButton.displayName = 'TextIconButton';
